import { conversionFor } from './unit-conversion'
import type { MatchingSearch } from '../matching-status'
export type AdditionalField = { code?: string; label: string; value: ReviewValue; certainty: 'confirmed' | 'uncertain'; page?: number; note?: string }
export type ReviewValue = string | number | null
export type LineField = 'name' | 'article' | 'unit' | 'quantity' | 'price' | 'amount' | 'vat_rate' | 'vat_amount' | 'total'
export interface DocumentLineMatch extends Partial<Record<LineField, ReviewValue>> {
  base_unit?: string
  base_coefficient?: number
  product_id?: string
  variant_id?: string
  variant_name?: string
  unit_id?: string
  source_kind?: 'document'
  source_line_id?: string
  request_position_uuid?: string
  amount_basis?: 'net' | 'gross' | 'unknown'
  dataset: 'requests' | 'orders' | 'receipts'
  document_number: string
  line_number: number
  name: string
  article?: ReviewValue
  unit?: ReviewValue
  quantity?: ReviewValue
  price?: ReviewValue
  amount?: ReviewValue
  total?: ReviewValue
}
export interface CatalogProductMatch extends Partial<Record<LineField, ReviewValue>> {
  source_kind: 'catalog'
  variant_id?: string
  variant_name?: string
  amount_basis?: never
  product_id: string
  unit_id?: string
  name: string
  code?: string
}
export type LineMatch = DocumentLineMatch | CatalogProductMatch
export const isCatalogMatch = (match: LineMatch): match is CatalogProductMatch => match.source_kind === 'catalog'
export interface ReviewLine {
  product_conflict?: string
  history_product_id?: string
  additional_fields?: AdditionalField[]
  amount_basis?: 'net' | 'gross' | 'unknown'
  line_number: number
  name?: ReviewValue
  article?: ReviewValue
  unit?: ReviewValue
  quantity?: ReviewValue
  price?: ReviewValue
  amount?: ReviewValue
  vat_rate?: ReviewValue
  vat_amount?: ReviewValue
  total?: ReviewValue
  page?: number
  status: 'matched' | 'review' | 'missing'
  issues: Array<{ field: LineField | 'match'; kind: 'recognition' | 'difference' | 'missing'; message: string }>
  match?: LineMatch
  candidates?: DocumentLineMatch[]
}
export interface RecognizedDocument {
  recognition_summary?: { complete: boolean; pages_total?: number; pages_read?: number[]; missing_pages?: number[]; blocking_problems?: string[] }
  matching_search?: MatchingSearch
  additional_fields?: AdditionalField[]
  source_name: string
  source_file?: string
  source_files?: string[]
  pages?: number[]
  document_number?: string
  document_date?: string
  supplier?: string
  buyer?: string
  warehouse?: string
  currency?: string
  amount?: ReviewValue
  amount_basis?: 'net' | 'gross' | 'unknown'
  subtotal?: ReviewValue
  vat_amount?: ReviewValue
  total?: ReviewValue
  conflicts?: string[]
  items?: ReviewLine[]
  matches: Array<{ dataset: 'requests' | 'orders' | 'receipts'; number: string; confidence: 'high' | 'uncertain' }>
}
export interface LineDraft {
  product_conflict?: string
  history_product_id?: string
  additional_request?: DocumentLineMatch
  excess_confirmation?: { quantity: string; factor: number; request_position_uuid: string; intent_hash: string }
  allocations?: Array<{ quantity: string; match: DocumentLineMatch; unit_confirmation?: { factor: number } }>
  amount_basis?: 'net' | 'gross' | 'unknown'
  field_values?: Record<string, ReviewValue>
  additional_values?: Record<string, ReviewValue>
  remember_product?: boolean
  remember_unit?: boolean
  memory_conflicts?: Array<{kind: 'product' | 'unit'; existing_id: string; existing_target: any; target: any}>
  memory_resolution?: Partial<Record<'product' | 'unit', {action: 'replace' | 'document'; existing_id: string}>>
  unit_confirmation?: { factor: number }
  memory_product_id?: string
  memory_unit_id?: string
  memory_reused?: boolean
  memory_notice?: string
  line_number: number
  values: Partial<Record<LineField, ReviewValue>>
  resolutions?: Record<string, 'accepted' | 'corrected'>
  checked: boolean
  match_index?: number
  manual_match?: LineMatch
}
export interface DocumentDraft {
  recognition_confirmed?: boolean
  field_values?: Record<string, ReviewValue>
  additional_values?: Record<string, ReviewValue>
  memory_notice?: string
  matching_mode?: 'required' | 'unlinked'
  header: Partial<Record<'document_number' | 'document_date' | 'supplier' | 'warehouse' | 'buyer' | 'currency' | 'subtotal' | 'vat_amount' | 'total', string>>
  items: LineDraft[]
  resolved_conflicts: number[]
}
export interface ReviewDocument {
  id: string
  message_id: string
  original: RecognizedDocument
  draft: DocumentDraft
  status: 'review' | 'confirmed'
  version: number
  updated_at: string
  updated_by?: number
  reviewer_name?: string
}
export interface SourceFile { name: string; path: string; mime_type?: string; size?: number; chat_id?: string }
export const valuesFor = (line: ReviewLine, draft?: LineDraft) => ({ ...line, ...draft?.values, ...(draft?.amount_basis ? { amount_basis: draft.amount_basis } : {}) })
export const matchFor = (line: ReviewLine, draft?: LineDraft) => draft?.allocations?.[0]?.match || draft?.manual_match || (draft?.match_index != null ? line.candidates?.[draft.match_index] : line.match)
export const lineMatchState = (line: ReviewLine, draft?: LineDraft, allowUnlinked = false) => {
  if (matchFor(line, draft)) return { key: 'matched', label: 'Товар сопоставлен' }
  if (allowUnlinked) return { key: 'unlinked', label: 'Без связи с 1С' }
  if (line.candidates?.length) return { key: 'candidate', label: line.candidates.length === 1 ? 'Найден вариант — подтвердите товар' : 'Найдены варианты — выберите товар' }
  return { key: 'missing', label: 'Товар в 1С не найден' }
}
export const confirmSingleCandidate = (line: ReviewLine, draft: LineDraft): LineDraft =>
  !matchFor(line, draft) && line.candidates?.length === 1
    ? { ...draft, match_index: 0, manual_match: undefined, checked: true, remember_product: line.candidates[0].dataset !== 'requests', unit_confirmation: undefined, memory_product_id: undefined, memory_unit_id: undefined }
    : draft
export const canConfirmProduct = (line: ReviewLine, draft?: LineDraft) => !!draft && !draft.checked
  && (!!matchFor(line, draft) || line.candidates?.length === 1)
export const confirmProduct = (line: ReviewLine, draft: LineDraft): LineDraft => {
  if (!canConfirmProduct(line, draft)) return draft
  const selected = confirmSingleCandidate(line, draft)
  const match = matchFor(line, selected)!
  return { ...selected, checked: true, remember_product: selected.remember_product ?? (!isCatalogMatch(match) && match.dataset !== 'requests') }
}
export const confirmAllProducts = (lines: ReviewLine[], draft: DocumentDraft): DocumentDraft => ({
  ...draft,
  items: draft.items.map(item => {
    const line = lines.find(row => row.line_number === item.line_number)
    return line ? confirmProduct(line, item) : item
  }),
})
export const displayValue = (value: ReviewValue | undefined) => value == null || value === '' ? '—' : String(value)
export const reviewDate = (value?: string) => {
  if (!value) return 'Не определена'
  const parts = value.split('T')[0].split('-')
  return parts.length === 3 && parts[0].length === 4 ? parts.reverse().join('.') : value
}
export const numberValue = (value: ReviewValue | undefined) => value == null || value === '' ? null : Number(String(value).replace(',', '.'))
export const moneyValue = (value: ReviewValue | undefined) => {
  const number = numberValue(value)
  return number != null && Number.isFinite(number) ? number.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
}
export const issueResolved = (line: ReviewLine, draft: LineDraft | undefined, index: number, allowUnlinked = false) => {
  const issue = line.issues[index]
  if (issue.field === 'match') return allowUnlinked || !!matchFor(line, draft)
  if (issue.field === 'unit' && issue.kind === 'difference' && conversionFor(valuesFor(line, draft).unit, matchFor(line, draft), draft?.unit_confirmation)) return true
  const value = valuesFor(line, draft)[issue.field]
  if (value == null || String(value).trim() === '') return false
  const action = draft?.resolutions?.[index]
  return action === 'accepted' || (action === 'corrected' && String(value) !== String(line[issue.field] ?? ''))
}
export const rowHasDataQuestions = (line: ReviewLine, draft?: LineDraft, allowUnlinked = false) =>
  ['name', 'unit', 'quantity', 'price', 'amount'].some(key => valuesFor(line, draft)[key as LineField] == null || valuesFor(line, draft)[key as LineField] === '')
  || line.issues.some((issue, index) => issue.field !== 'match' && !issueResolved(line, draft, index, allowUnlinked))
export const rowHasQuestions = (line: ReviewLine, draft?: LineDraft, allowUnlinked = false) => (!allowUnlinked && !matchFor(line, draft))
  || rowHasDataQuestions(line, draft, allowUnlinked)
  || line.issues.some((_, index) => !issueResolved(line, draft, index, allowUnlinked))
export const rowNeedsWork = (line: ReviewLine, draft?: LineDraft, allowUnlinked = false) => {
  return !draft?.checked || rowHasQuestions(line, draft, allowUnlinked)
}
export const pendingCount = (document: ReviewDocument, draft = document.draft) =>
  (['supplier', 'document_number', 'document_date'] as const).filter(field => !String(draft.header[field] ?? document.original[field] ?? '').trim()).length
    + (document.original.items || []).filter(line => rowNeedsWork(line, draft.items.find(item => item.line_number === line.line_number), draft.matching_mode === 'unlinked')).length
export const totalsCheck = (document: RecognizedDocument, draft: DocumentDraft) => {
  document = { ...document, ...draft.header }
  if (!document.items?.length) return null
  const rows = document.items.map(line => ({ line, values: valuesFor(line, draft.items.find(row => row.line_number === line.line_number)) }))
  const basis = rows.map(({ values }) => values.amount_basis ?? document.amount_basis)
  const field = basis.every(value => value === 'net') ? 'amount' : basis.every(value => value === 'gross') ? 'amount' : 'total'
  const isNet = field === 'amount' && basis.every(value => value === 'net')
  const expected = numberValue(isNet ? document.subtotal ?? (document.amount_basis === 'net' ? document.amount : null) : document.total ?? (document.amount_basis === 'gross' ? document.amount : null))
  const values = rows.map(({ values }) => numberValue(values[field]))
  if (expected == null || values.some(value => value == null || !Number.isFinite(value))) return null
  const actual = values.reduce<number>((sum, value) => sum + (value || 0), 0)
  return { expected, actual, matches: Math.abs(actual - expected) < 0.011 }
}
export const draftForConfirmation = (document: ReviewDocument, draft = document.draft): DocumentDraft => ({
  ...draft,
  items: draft.items.map(row => {
    const line = document.original.items?.find(item => item.line_number === row.line_number)
    if (!line || !row.checked || (draft.matching_mode !== 'unlinked' && !matchFor(line, row))) return row
    const values = valuesFor(line, row)
    const resolutions = { ...row.resolutions }
    line.issues.forEach((issue, index) => {
      if (issue.field !== 'match' && !issueResolved(line, row, index, draft.matching_mode === 'unlinked')
        && values[issue.field] != null && String(values[issue.field]).trim() !== '') resolutions[index] = 'accepted'
    })
    return { ...row, resolutions }
  }),
})
export const reviewCompletionState = (document: ReviewDocument, draft = document.draft) => {
  const lines = document.original.items || []
  const confirmedProducts = lines.filter(line => {
    const row = draft.items.find(item => item.line_number === line.line_number)
    return row?.checked && (draft.matching_mode === 'unlinked' || !!matchFor(line, row))
  }).length
  const canStart = lines.length > 0 && confirmedProducts === lines.length
  return { confirmedProducts, totalProducts: lines.length, canStart,
    canComplete: canStart && !document.original.recognition_summary?.blocking_problems?.length && !document.original.recognition_summary?.missing_pages?.length && pendingCount(document, draftForConfirmation(document, draft)) === 0 && totalsCheck(document.original, draft)?.matches !== false }
}
export const documentState = (document: ReviewDocument, draft = document.draft, dirty = false) => {
  if (document.status === 'confirmed' && !dirty) return draft.matching_mode === 'unlinked'
    ? { key: 'unlinked', label: 'Проверен без связи', color: 'blue' }
    : { key: 'confirmed', label: 'Проверен', color: 'success' }
  const states = (document.original.items || []).map(line => lineMatchState(line, draft.items.find(item => item.line_number === line.line_number), draft.matching_mode === 'unlinked'))
  if (states.some(state => state.key === 'missing')) return { key: 'missing', label: 'Не найдено соответствие', color: 'error' }
  if (states.some(state => state.key === 'candidate')) return { key: 'differences', label: 'Подтвердите товары', color: 'gold' }
  if (!(document.original.items || []).length
    || (['supplier', 'document_number', 'document_date'] as const).some(key => !String(draft.header[key] ?? document.original[key] ?? '').trim())
    || document.original.items?.some(line => rowHasQuestions(line, draft.items.find(item => item.line_number === line.line_number), draft.matching_mode === 'unlinked'))
    || totalsCheck(document.original, draft)?.matches === false) return { key: 'differences', label: 'Есть вопросы', color: 'gold' }
  return { key: 'ready', label: 'Готов к проверке', color: 'default' }
}
export const quantityDifference = (line: ReviewLine, draft?: LineDraft) => {
  const match = matchFor(line, draft), values = valuesFor(line, draft)
  const actual = numberValue(values.quantity), expected = numberValue(match?.quantity)
  const conversion = conversionFor(values.unit, match, draft?.unit_confirmation)
  if (!match || !conversion || actual == null || expected == null || !Number.isFinite(actual) || !Number.isFinite(expected)) return null
  const converted = actual * conversion.factor
  return Math.abs(converted - expected) < 0.000001 ? null : converted - expected
}
export const parseRouting = (content: unknown): { schema_version: number; documents: RecognizedDocument[] } | null => {
  if (typeof content !== 'string' || content.length > 2_000_000) return null
  const lines = content.split(/\r?\n/)
  const start = lines.findIndex(line => line.trim() === '```erp-supply-routing')
  if (start < 0) return null
  const end = lines.findIndex((line, index) => index > start && line.trim() === '```')
  if (end < 0) return null
  try {
    const value = JSON.parse(lines.slice(start + 1, end).join('\n'))
    return [1, 2].includes(value?.schema_version) && Array.isArray(value.documents) ? value : null
  } catch { return null }
}

export const addAdditionalRequest = (line: ReviewLine, draft: LineDraft, additional: LineMatch): LineDraft => {
  const primary = matchFor(line, draft)
  if (isCatalogMatch(additional) || additional.dataset !== 'requests' || !additional.request_position_uuid
    || !primary?.product_id || primary.product_id !== additional.product_id || (!isCatalogMatch(primary) && (primary.variant_id || '') !== (additional.variant_id || '')))
    throw new Error('Выберите заявку с тем же товаром и характеристикой, что и в текущем основании')
  if (!isCatalogMatch(primary) && primary.request_position_uuid === additional.request_position_uuid)
    throw new Error('Эта заявка уже выбрана. Для излишка нужна дополнительная заявка')
  if (isCatalogMatch(primary)) return { ...draft, manual_match: additional, match_index: undefined,
    allocations: undefined, additional_request: undefined, unit_confirmation: undefined,
    excess_confirmation: undefined, checked: false, resolutions: {} }
  return { ...draft, additional_request: additional, excess_confirmation: undefined }
}

/** Only repeat a catalog choice inside this document. Keep every line's quantities,
 * prices, conversions and confirmation independent. */
export function identicalUnselectedLines(source: ReviewLine, edit: LineDraft, lines: ReviewLine[], drafts: LineDraft[]) {
  const key = (line: ReviewLine, draft?: LineDraft) => {
    const values = valuesFor(line, draft)
    if (!values.name || !values.unit || line.product_conflict || draft?.product_conflict) return null
    const fields = (line.additional_fields || []).map((f, index) => [f.code, f.label, draft?.additional_values?.[index] ?? f.value])
    const variants = (line.candidates || []).map(c => [c.product_id, c.variant_id, c.variant_name, c.unit_id]).map(v => JSON.stringify(v)).sort()
    return JSON.stringify([values.name, values.article || '', values.unit, fields, draft?.field_values || {}, variants])
  }
  const originalKey = key(source, edit)
  if (!originalKey || !edit.manual_match || !isCatalogMatch(edit.manual_match)) return []
  return lines.filter(line => {
    const row = drafts.find(d => d.line_number === line.line_number)
    return row && line.line_number !== source.line_number && !row.checked && !matchFor(line, row)
      && !row.memory_notice && !row.additional_request && !row.unit_confirmation && !row.memory_conflicts?.length
      && key(line, row) === originalKey
  }).map(line => line.line_number)
}
